import type { TFunction } from "i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { useOnDemandEventWrites } from "@/services/irrigation-scheduling/use-on-demand-event-writes";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { SIZE } from "@/configs/sizes";
import { useLocale } from "@/hooks/use-locale";
import { useSnackbar } from "@/hooks/use-snackbar/use-snackbar";
import { NAMESPACES } from "@/i18n/constants";
import { useOnDemandIrrigationEventTracker } from "@/services/analytics/irrigation/use-on-demand-irrigation-event-tracker";
import { useFormatDuration } from "@/utils/format-duration";
import { EVENT_DURATION_LOWER_BOUND_MS, EVENT_DURATION_UPPER_BOUND_MS } from "../on-demand-irrigation-constants";
import { FormContainer } from "./form-container";
import { FormValidationMessage } from "./form-validation-message";
import { ResponseTimeHint } from "./response-time-hint";
import { TimeInput } from "./time-input";

const LARGE_STEP_MS = 15 * minuteMs;
const SMALL_STEP_MS = 5 * minuteMs;
const TOOLTIP_DELAY = 150;

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
};

/** Returns an error message if the duration is out of bounds, or null if valid */
function validateEventDuration(
    tOnDemandValidationErrors: TFunction,
    durationMs: number,
    eventStartTime: Date,
    nowMs: number,
): string | null {
    if (durationMs < EVENT_DURATION_LOWER_BOUND_MS)
        return tOnDemandValidationErrors("duration-too-short", {
            minDurationMinutes: EVENT_DURATION_LOWER_BOUND_MS / minuteMs,
        });
    if (durationMs > EVENT_DURATION_UPPER_BOUND_MS)
        return tOnDemandValidationErrors("duration-too-long", {
            maxDurationHours: EVENT_DURATION_UPPER_BOUND_MS / hourMs,
        });

    // New event end time cannot end before current time
    if (eventStartTime.getTime() + durationMs < nowMs)
        return tOnDemandValidationErrors("new-event-ends-before-current-time");
    return null;
}

const roundToNearestMinute = (durationMs: number) => Math.round(durationMs / minuteMs) * minuteMs;

// Ordered left-to-right: [-15m, -5m, +5m, +15m]
const POSITIVE_STEP_BUTTONS = [
    { label: "+5m", value: SMALL_STEP_MS, icon: ChevronRight },
    { label: "+15m", value: LARGE_STEP_MS, icon: ChevronsRight },
] as const;

const NEGATIVE_STEP_BUTTONS = [
    { label: "-15m", value: -LARGE_STEP_MS, icon: ChevronsLeft },
    { label: "-5m", value: -SMALL_STEP_MS, icon: ChevronLeft },
] as const;

interface EditEventFormProps {
    onClose: () => void;
    recordId: string;
    /** The newest fetched version, sent as the version the edit was made against. */
    versionId?: number;
    zoneId: string;
    currentEventDuration: number; // ms
    eventEndTime: Date;
    eventStartTime: Date;
    responseTimeMs?: number;
    onPendingNewEndTimeChange?: (newEndTime: Date | null) => void;
}

export function EditEventForm({
    onClose,
    recordId,
    versionId,
    zoneId,
    currentEventDuration,
    eventEndTime,
    eventStartTime,
    responseTimeMs,
    onPendingNewEndTimeChange,
}: EditEventFormProps) {
    // The change the user has asked for, not the duration it lands on. Holding the delta is what
    // makes a refetch harmless: the baseline moves, the delta does not, so a server-side change can
    // never read as a pending edit. Zero means nothing has been asked for, which is why there is no
    // separate "touched" flag to keep in step.
    const [durationDeltaMs, setDurationDeltaMs] = useState(0);
    const baselineDurationMs = roundToNearestMinute(currentEventDuration);
    // A refetch can drop the baseline below a pending decrease, leaving the stored change asking for
    // less than nothing. Everything downstream reads the normalized change instead, so the
    // projection, the copy and the next step agree with the duration actually shown.
    const clampToBaseline = (deltaMs: number) => Math.max(-baselineDurationMs, deltaMs);
    const effectiveDeltaMs = clampToBaseline(durationDeltaMs);
    // What the event would run for if saved now: the server's current duration plus that change.
    const durationMs = baselineDurationMs + effectiveDeltaMs;
    const onDemandIrrigationEventTracker = useOnDemandIrrigationEventTracker(zoneId);
    const { editOnDemandEvent } = useOnDemandEventWrites();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.edit-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });
    const formatDuration = useFormatDuration();

    const locale = useLocale();
    const eventEndTimeMs = eventEndTime.getTime();

    useEffect(() => {
        onPendingNewEndTimeChange?.(new Date(eventEndTimeMs + effectiveDeltaMs));
    }, [effectiveDeltaMs, eventEndTimeMs, onPendingNewEndTimeChange]);

    const projectedEndTimeLabel = new Date(eventEndTime.getTime() + effectiveDeltaMs).toLocaleTimeString(
        locale,
        TIME_FORMAT_OPTIONS,
    );
    const startTimeLabel = eventStartTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const timeDeltaLabel = `${formatDuration(roundToNearestMinute(Math.abs(effectiveDeltaMs)), { includeSecondsWithMinutes: false })}`;
    const eventDurationLabel =
        effectiveDeltaMs >= 0
            ? t("buttons.increase-duration", { timeDeltaLabel })
            : t("buttons.decrease-duration", { timeDeltaLabel });
    const validationError = validateEventDuration(tOnDemandValidationErrors, durationMs, eventStartTime, Date.now());

    // Clamped on the resulting duration rather than the delta, so a step can never ask for less
    // than nothing.
    // Steps from the normalized change, so the first click after a baseline drop moves the duration
    // rather than spending itself unwinding a change that is already out of range.
    const modifyDuration = (stepMs: number) =>
        setDurationDeltaMs((prev) => clampToBaseline(clampToBaseline(prev) + stepMs));

    /** The time input is absolute, so a typed duration becomes the change that reaches it. */
    const setDurationFromInput = (nextDurationMs: number) => setDurationDeltaMs(nextDurationMs - baselineDurationMs);

    const handleClickEditEvent = async () => {
        const response = await editOnDemandEvent({ recordId, versionId, durationMs });
        if (response) {
            onDemandIrrigationEventTracker.trackUpdate({ eventId: recordId, durationMs });
            enqueueSuccessSnackbar(t("snackbar.success", { timeDeltaLabel }));
            onClose();
        } else {
            enqueueErrorSnackbar(t("snackbar.error"));
            onDemandIrrigationEventTracker.trackUpdateFail(recordId);
        }
    };

    return (
        <FormContainer title={t("title")} onClose={onClose} variant={"edit"}>
            <div className={"flex flex-row gap-2 xs:gap-3 w-full items-center justify-center"}>
                {NEGATIVE_STEP_BUTTONS.map((config) => (
                    <Button
                        key={config.label}
                        tooltip={config.label}
                        tooltipDelay={TOOLTIP_DELAY}
                        size={"icon-xxl"}
                        variant={"outline"}
                        disabled={
                            durationMs + config.value < EVENT_DURATION_LOWER_BOUND_MS ||
                            // Disable if the new event would end before the current time
                            eventStartTime.getTime() + durationMs + config.value < Date.now()
                        }
                        onClick={() => modifyDuration(config.value)}
                    >
                        <Icon iconClassName={"p-0"} icon={config.icon} size={SIZE.LG} />
                    </Button>
                ))}
                <TimeInput
                    className={
                        "py-3 px-2 xs:p-3 w-full min-w-28 max-w-34 flex flex-col items-center gap-1 relative xs:mx-2"
                    }
                    inputClassName={"text-center xs:text-lg xs:tracking-wider"}
                    value={durationMs}
                    onChange={setDurationFromInput}
                />
                {POSITIVE_STEP_BUTTONS.map((config) => (
                    <Button
                        key={config.label}
                        tooltip={config.label}
                        tooltipDelay={TOOLTIP_DELAY}
                        size={"icon-xxl"}
                        variant={"outline"}
                        disabled={durationMs + config.value > EVENT_DURATION_UPPER_BOUND_MS}
                        onClick={() => modifyDuration(config.value)}
                    >
                        <Icon iconClassName={"p-0"} icon={config.icon} size={SIZE.LG} />
                    </Button>
                ))}
            </div>

            <div className={"flex flex-col w-full gap-1"}>
                <div className={"text-muted-foreground flex flex-row items-center justify-between w-full gap-2"}>
                    <span>{t("event-time")}:</span>
                    <p className={"rounded-lg"}>
                        {startTimeLabel} - {projectedEndTimeLabel}
                    </p>
                </div>
            </div>

            {validationError && <FormValidationMessage errorMessage={validationError} />}

            <div className={"flex flex-col items-center jusity-center gap-1"}>
                <Button
                    className={"w-full p-3"}
                    variant={"info"}
                    disabled={effectiveDeltaMs === 0 || validationError !== null}
                    onClick={handleClickEditEvent}
                >
                    {eventDurationLabel}
                </Button>
                <ResponseTimeHint responseTimeMs={responseTimeMs} className={"mt-0"} />
            </div>
        </FormContainer>
    );
}
